// CAI statusLine provider for Claude Code.
//
// Registered as the `statusLine` command in ~/.claude/settings.json, Claude Code
// invokes this on each message with a JSON payload on stdin that carries the
// LIVE context-window fill (`context_window.used_percentage`). We write that
// exact value to the watchdog bridge file (/tmp/claude-ctx-<session>.json) and
// print a compact status line. The watchdog (and the GUI) then read an accurate
// live signal instead of the old, over-estimating transcript heuristic.

import { writeFileSync, readFileSync, existsSync, mkdirSync } from "node:fs";
import { homedir } from "node:os";
import { join, dirname } from "node:path";
import { bridgePath } from "./signal.js";

export interface StatuslinePayload {
  session_id?: string;
  cwd?: string;
  model?: { display_name?: string };
  workspace?: { current_dir?: string; project_dir?: string };
  context_window?: {
    used_percentage?: number;
    remaining_percentage?: number;
    total_input_tokens?: number;
    context_window_size?: number;
  };
}

/** Extract the live used-% from the payload, or null if not yet available
 *  (e.g. before the first API call, or right after /compact). */
export function usedPercentOf(payload: StatuslinePayload): number | null {
  const cw = payload.context_window;
  if (!cw) return null;
  if (typeof cw.used_percentage === "number") return cw.used_percentage;
  if (typeof cw.remaining_percentage === "number") return 100 - cw.remaining_percentage;
  return null;
}

/** Write the bridge file (best-effort) and return the status line string. */
export function writeStatuslineBridge(payload: StatuslinePayload, now: number): string {
  const used = usedPercentOf(payload);
  const session = payload.session_id;
  if (session && used !== null) {
    try {
      writeFileSync(
        bridgePath(session),
        JSON.stringify({
          used_percentage: used,
          session_id: session,
          // The session's working dir, so the watchdog can match a bridge to its
          // project when several sessions run at once.
          cwd: payload.workspace?.current_dir ?? payload.workspace?.project_dir ?? payload.cwd ?? null,
          total_input_tokens: payload.context_window?.total_input_tokens ?? null,
          context_window_size: payload.context_window?.context_window_size ?? null,
          ts: now,
        })
      );
    } catch {
      /* best-effort — never break the status line on a write failure */
    }
  }
  return renderStatusline(payload, used);
}

/** Compact, dependency-free status line: model + live context fill. */
export function renderStatusline(payload: StatuslinePayload, used: number | null): string {
  const model = payload.model?.display_name ?? "Claude";
  if (used === null) return `cai · ${model}`;
  const pct = Math.round(used);
  const bar = used >= 75 ? "🔴" : used >= 55 ? "🟡" : "🟢";
  return `cai · ${model} · ${bar} ctx ${pct}%`;
}

function settingsPath(): string {
  return join(homedir(), ".claude", "settings.json");
}

function readSettings(path: string): Record<string, unknown> {
  if (!existsSync(path)) return {};
  try {
    return JSON.parse(readFileSync(path, "utf-8")) as Record<string, unknown>;
  } catch {
    return {};
  }
}

/** Register `cai statusline` as Claude Code's statusLine command (global). */
export function installStatusline(command = "cai statusline"): { path: string; already: boolean } {
  const path = settingsPath();
  const settings = readSettings(path);
  const existing = settings.statusLine as { command?: string } | undefined;
  const already = existing?.command === command;
  settings.statusLine = { type: "command", command };
  mkdirSync(dirname(path), { recursive: true });
  writeFileSync(path, JSON.stringify(settings, null, 2) + "\n");
  return { path, already };
}

/** Remove the CAI statusLine registration if it points at us. */
export function uninstallStatusline(command = "cai statusline"): { path: string; removed: boolean } {
  const path = settingsPath();
  const settings = readSettings(path);
  const existing = settings.statusLine as { command?: string } | undefined;
  if (existing?.command === command) {
    delete settings.statusLine;
    writeFileSync(path, JSON.stringify(settings, null, 2) + "\n");
    return { path, removed: true };
  }
  return { path, removed: false };
}
